import { useState, useEffect } from 'react';
import { APIClient } from '../services/api';
export function useSession(config) {
    const [session, setSession] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const apiClient = new APIClient(config);
    const createSession = async () => {
        if (!config.tenantId || !config.userId) {
            setError('tenantId and userId are required');
            return;
        }
        setLoading(true);
        setError(null);
        try {
            const authAssertion = config.authToken || 'anonymous';
            const newSession = await apiClient.createSession(config.tenantId, config.userId, authAssertion);
            setSession(newSession);
        }
        catch (err) {
            setError(err instanceof Error ? err.message : 'Failed to create session');
        }
        finally {
            setLoading(false);
        }
    };
    const refreshToken = async () => {
        if (!session)
            return;
        try {
            const result = await apiClient.refreshToken(session.sessionId);
            setSession({
                ...session,
                ephemeralToken: result.ephemeralToken,
                expiresAt: result.expiresAt,
            });
        }
        catch (err) {
            setError(err instanceof Error ? err.message : 'Failed to refresh token');
        }
    };
    const endSession = async () => {
        if (!session)
            return;
        try {
            await apiClient.endSession(session.sessionId);
            setSession(null);
        }
        catch (err) {
            setError(err instanceof Error ? err.message : 'Failed to end session');
        }
    };
    // Auto-refresh token before expiration
    useEffect(() => {
        if (!session)
            return;
        const expiresAt = new Date(session.expiresAt).getTime();
        const now = Date.now();
        const timeUntilExpiry = expiresAt - now;
        const refreshTime = timeUntilExpiry - 5 * 60 * 1000; // Refresh 5 minutes before expiry
        if (refreshTime > 0) {
            const timer = setTimeout(() => {
                refreshToken();
            }, refreshTime);
            return () => clearTimeout(timer);
        }
    }, [session]);
    return {
        session,
        loading,
        error,
        createSession,
        refreshToken,
        endSession,
    };
}
